import type { ItemKind, Letter } from "../types";

import { letters } from "./letters";

/**
 * `letters.ts` is the single source of truth for the base content. This
 * module builds the id/glyph lookup maps every other consumer (the parser,
 * SRS, session assembler) reads from.
 */

function mapById(items: Letter[]): ReadonlyMap<string, Letter> {
  return new Map(items.map((letter) => [letter.id, letter]));
}

function mapByGlyph(kind: ItemKind): ReadonlyMap<string, Letter> {
  return new Map(
    letters
      .filter((letter) => letter.kind === kind)
      .map((letter) => [letter.glyph, letter]),
  );
}

/** All letters indexed by their stable id. */
export const letterById: ReadonlyMap<string, Letter> = mapById(letters);

/** Consonants indexed by their glyph. */
export const consonantByGlyph: ReadonlyMap<string, Letter> =
  mapByGlyph("consonant");

/** Independent vowels indexed by their glyph. */
export const vowelByGlyph: ReadonlyMap<string, Letter> = mapByGlyph("vowel");

/** Matras (dependent vowel signs) indexed by their combining sign. */
export const matraBySign: ReadonlyMap<string, Letter> = mapByGlyph("matra");

/** Diacritical signs indexed by their glyph. */
export const signByGlyph: ReadonlyMap<string, Letter> = mapByGlyph("sign");

/** Devanagari numerals indexed by their glyph. */
export const numeralByGlyph: ReadonlyMap<string, Letter> =
  mapByGlyph("numeral");
